import { isValidEmail } from '@frontend/duck-tape';
import type { CommonModalProps } from '@frontend/web-react';
import { Button, Icon, Modal, Pill, Text, TextInput, XStack, YStack, showToast } from '@frontend/web-react';
import { useAnalytics, useGetShareUrlMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useEffect, useState } from 'react';

type ShareTaskModalProps = CommonModalProps & {
  taskId: string;
};

export const ShareTaskModal = ({ isOpen, onClose, taskId }: ShareTaskModalProps) => {
  const { track } = useAnalytics();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [getShareUrl, { isLoading }] = useGetShareUrlMutation();

  useEffect(() => {
    if (isOpen) {
      setEmails([]);
      setEmailInput('');
      setShareUrl(null);
      track({ data: { taskId }, event: 'modalOpened', topic: 'shareTask' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      showToast({ title: 'Please enter a valid email address', variant: 'danger' });
      return;
    }

    if (emails.includes(trimmedEmail)) {
      showToast({ title: 'Email already added', variant: 'warning' });
      return;
    }

    setEmails([...emails, trimmedEmail]);
    setEmailInput('');
    track({ data: { emailCount: emails.length + 1, taskId }, event: 'emailAdded', topic: 'shareTask' });
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    const newEmails = emails.filter((email) => email !== emailToRemove);
    setEmails(newEmails);
    track({ data: { emailCount: newEmails.length, taskId }, event: 'emailRemoved', topic: 'shareTask' });
  };

  const handleShare = async () => {
    if (emails.length === 0) {
      showToast({ title: 'Please add at least one email address', variant: 'danger' });
      return;
    }

    await handleMutation({
      errorMessage: 'Failed to generate share link. Please try again.',
      mutation: () => getShareUrl({ emails, taskId }).unwrap(),
      onSuccess: (result) => {
        setShareUrl(result.url);
        track({ data: { emailCount: emails.length, emails, taskId }, event: 'linkCreated', topic: 'shareTask' });
      },
      showSuccessToast: false,
    });
  };

  const handleCopyUrl = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast({ title: 'Link copied to clipboard', variant: 'success' });
        track({ data: { taskId }, event: 'linkCopied', topic: 'shareTask' });
      } catch {
        showToast({ title: 'Failed to copy link', variant: 'danger' });
      }
    }
  };

  const handleClose = () => {
    setEmails([]);
    setEmailInput('');
    setShareUrl(null);
    onClose();
  };

  return (
    <Modal
      Footer={
        <XStack className="p-md" justifyContent="end">
          {shareUrl ? (
            <Button onClick={handleClose} variant="primary">
              Done
            </Button>
          ) : (
            <Button disabled={emails.length === 0} isLoading={isLoading} onClick={handleShare} variant="primary">
              Create Link
            </Button>
          )}
        </XStack>
      }
      Header={
        <XStack className="p-md border-b border-borderPrimary justify-between items-center">
          <Text type="h5">Share Task</Text>
          <Icon className="cursor-pointer" name="IconX" onClick={handleClose} />
        </XStack>
      }
      isOpen={isOpen}
      onClose={handleClose}
      padding="none"
      size="md"
    >
      <YStack className="p-md gap-md">
        {shareUrl ? (
          <YStack gap="md">
            <Text type="p-sm">
              Your link is ready! Share it with friends and family to show them how Duckbill handled this task for you.
            </Text>
            <XStack
              alignItems="center"
              className="border border-borderPrimary rounded-md p-md cursor-pointer hover:bg-neutral-50"
              gap="sm"
              onClick={handleCopyUrl}
            >
              <Text className="flex-1 truncate" type="p-sm">
                {shareUrl}
              </Text>
              <Icon name="IconCopy" size="sm" />
            </XStack>
            <Text color="tertiary" type="caption">
              Click to copy
            </Text>
          </YStack>
        ) : (
          <YStack gap="md">
            <Text type="p-sm">
              Show your friends what Duckbill can do! Add their email addresses below and we&apos;ll create a shareable
              link to this task thread.
            </Text>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddEmail();
              }}
            >
              <XStack alignItems="center" gap="sm">
                <TextInput
                  className="flex-1"
                  onChange={setEmailInput}
                  placeholder="Enter email address"
                  value={emailInput}
                />
                <Button disabled={!isValidEmail(emailInput.trim())} isForm size="md" variant="secondary">
                  Add
                </Button>
              </XStack>
            </form>
            {emails.length > 0 ? (
              <XStack className="flex-wrap" gap="sm">
                {emails.map((email) => (
                  <Pill key={email} onRemove={() => handleRemoveEmail(email)} withRemoveButton>
                    {email}
                  </Pill>
                ))}
              </XStack>
            ) : (
              <Text color="tertiary" type="caption">
                No emails added yet
              </Text>
            )}
          </YStack>
        )}
      </YStack>
    </Modal>
  );
};
